/**
 * Copyright (C) 2020, Merkur India.
 *
 * The program(s) herein may be used and/or copied only with
 * the written permission of Merkur India. or in accordance with
 * the terms and conditions stipulated in the agreement/contract
 * under which the program(s) have been supplied.
 *
 * $Id: BSSDBaseGameState.ts ,v 1.0 Exp $
 * Date Author Changes
 * 3 July 2020 Sujata Nethani Created
 *
 */
    ///<reference path="../../constants/GameConstants.ts"/>
///<reference path="../../constants/HRGameLayoutConstants.ts"/>
///<reference path="../../paytable/GamePaytable.ts"/>
///<reference path="../../paytable/PaytableStyle.ts"/>

namespace States {
    import Symbol = Symbols.Symbol;
    import ReelContainer = Reels.BSReelContainer;
    import BasicWinAnimation = WinAnimation.BasicWinAnimation;
    import SwitchableBackground = UIComponent.SwitchableBackground;
    import PaytableView = Paytable.PaytableView;
    import ReelAnimationPhaseId = Reels.ReelAnimationPhaseId;
    import GameActionResponseType = DTO.GameActionResponseType;
    import GameActionResponse = DTO.GameActionResponse;
    import PlayCommandClientPadding = GameFlow.Commands.BSPlayCommandClientPadding;
    import SlotGameActions = Game.Action.SlotGame;
    import Command = GameFlow.Command;
    import ResponsiveBackground = gameObjects.ResponsiveBackground;
    import VerticalAlignment = gameObjects.VerticalAlignment;
    import HideLogos = gameObjects.HideLogos;

    /**
     * The second BaseGameState used for the Desktop version
     * Its pretty much the same as the BaseGameState just using different graphics and having different positions
     * Its still needed to create an extra state since their can be different animations for winAnimations,reels etc. in mobile and desktop version
     */
    export class BSSDBaseGameState extends BaseGameState {
        private componentBuilder: Utility.ComponentBuilder;
        public layoutConstants: typeof BS.SDGameLayoutConstants = BS.SDGameLayoutConstants;
        /**
        * This is the first function thats called, after all assets for the state have been loaded
        * You can only access the PortalAPI and GameFrame at this stage of the state
        * everything else is not yet created
        * Most of the time their is no need to implement this method but its good to know its their :D
        */
        protected preCreate(): void {
            this.componentBuilder = new Utility.ComponentBuilder(this.game);
        }
        
        protected postCreate() {
            super.postCreate();
            for (let reel of this.reelContainer.reels) {
                reel.animation.removeAnimationPhase(reel.animation.getPhase(ReelAnimationPhaseId.REEL_BLUR_PHASE));
                reel.animation.removeAnimationPhase(reel.animation.getPhase(ReelAnimationPhaseId.REEL_CLEAR_FILTER_PHASE));
            }
            if (this.portalAPI.networkProvider.getLastResponse().responseType === GameActionResponseType.INIT) {
                Sounds.SoundManager.play("BlazingStarIntro");
            }
        }

        /**
         * Register all commands needed for the BaseGame
         */
        protected registerCommands(): {[x: string]: Command} {
            let commands = super.registerCommands();
            commands[SlotGameActions.PLAY] = new PlayCommandClientPadding();
            return commands;
        }
        
         public handleResult(response: GameActionResponse): void {
            if (Sounds.SoundManager.isPlaying("BlazingStarIntro")) {
                Sounds.SoundManager.stop("BlazingStarIntro");
            }
            super.handleResult(response);
        }
        
        /**
         * Adds Components to the provided layers
         */
        protected addComponentsToLayers(): void {
            super.addComponentsToLayers();
            this.addSunLogo(this.paytableLayer);
        }
        /**
         * Add sun logo
         */
        public addSunLogo(view: Phaser.Group) {
            let sunLogo = new Phaser.Image(this.game, BS.SDGameLayoutConstants.SUN_X, BS.SDGameLayoutConstants.SUN_Y, 'merkursonne_hd', 'sun_logo');
            view.addChild(sunLogo);
        }

        getZoomData(): { width: number; height: number; offsetX: number; offsetY: number; duration: number } {
            return {
                width:this.layoutConstants.GAME_WIDTH - BS.SDGameLayoutConstants.OFFSET_X*2,
                height:this.layoutConstants.GAME_HEIGHT - BS.SDGameLayoutConstants.OFFSET_Y*2,
                offsetX:BS.SDGameLayoutConstants.OFFSET_X,
                offsetY:BS.SDGameLayoutConstants.OFFSET_Y,
                duration:0
            }
            // return super.getZoomData();
        }

        resetInactivityTimer() {
            // super.resetInactivityTimer();
        }
        /**
         * Create and return the SwitchableBackground in this function
         * For the BaseGame theirs mostly only one background needed
         * @Override
         */
        protected createBackground(): SwitchableBackground {
            this.gameContainer.setGameArea(
                this.layoutConstants.GAME_WIDTH - BS.SDGameLayoutConstants.OFFSET_X*2,
                this.layoutConstants.GAME_HEIGHT - BS.SDGameLayoutConstants.OFFSET_Y*2,
                BS.SDGameLayoutConstants.OFFSET_X,
                BS.SDGameLayoutConstants.OFFSET_Y,
            )
            let bg = new ResponsiveBackground(
                this.game,
                this,
                {key:"background_landscape", frame:"BLSU_hg", scaleFit:{verticalAlignment:VerticalAlignment.CENTER}},
                {key:"background",frame:"BLSU_bg", scaleFit:{verticalAlignment:VerticalAlignment.CENTER}},
                {key:"reel_logo", frame:"BSLU_Reel", x:0, y:-29},
                {key:"reel_logo",frame:"BS_Logo", x:460, y:35, scaleX:0.8, scaleY:0.8, anchorX:0.5, anchorY:0.5},
                {key:"reel_logo",frame:"BS_Logo_big", x:460, y:120, scaleX:0.7, scaleY:0.7, anchorX:0.5, anchorY:0.4},
            )
            bg.setAspectRatioLogoSwitch(0.9);
            return new SwitchableBackground(bg);
        }

        /**
         * Create all the symbols for the game in this method together with its reel and win animations
         * To make the creation easier you can use the utility function of the SymbolBuilder
         * @Override
         */
        protected createSymbols(): Symbol[] {
            return this.componentBuilder.createSDSymbols();
        }

        /**
         * Creates the winAnimation including winLines and lineTags
         * @Override
         */
        protected createWinAnimation(): BasicWinAnimation {
            return this.componentBuilder.createWinAnimation(this.availableSymbols, undefined);
        }

        /**
         * Create the ReelContainer add all the reels and sounds for it
         * @Override
         */
        protected createReelContainer(): ReelContainer {
            return this.componentBuilder.createReelContainer(this.availableSymbols);
        }

        /**
         * Create the paytableView
         * @Override
         */
        protected createPaytable(): PaytableView {
            let paytable = this.componentBuilder.createPaytable(this.stateDTO);
            (this.background.basegameBackground as ResponsiveBackground).linkPaytableAndLogoVisibility(paytable, HideLogos.LANDSCAPE);
            return paytable;
        }

        /**
         * Specify the width of the HD_State, in this case we aim for 720p
         * @returns {number}
         */
        public getWidth(): number {
            return BS.SDGameLayoutConstants.GAME_WIDTH;
        }

        /**
         * Specify the height of the HD_State
         * @returns {number}
         */
        public getHeight(): number {
            return BS.SDGameLayoutConstants.GAME_HEIGHT;
        }
    }
}
